import React, { useContext, useEffect, useState } from "react";
import styles from "./WorkSections.module.scss";
import Label from "../../../../../components/Admin/Label/Label";
import Input from "../../../../../components/Admin/Input/Input";
import ThemedButton from "../../../../../components/Admin/ThemedButton/ThemedButton";
import Toggle from "../../../../../components/Admin/Toggle/Toggle";
import { MessageContext } from "../../../../../context/message/MessageContext";
import useWorkSection from "../../../../../data-access/useWorkSection";
import Select from "../../../../../components/Admin/Select/Select";
import useProject from "../../../../../data-access/useProject";

const WorkSections = ({ element, setElement, workSection }) => {
  const [input, setInput] = useState({ name: "", projects: [], link: "" });
  const [newWorkSection, setNewWorkSection] = useState(!element);
  const { addError, addSuccess } = useContext(MessageContext);

  const {
    refetchWorkSection,
    createWorkSection,
    createWorkSectionLoading,
    updateWorkSection,
    updateWorkSectionLoading,
    deleteWorkSection,
    deleteWorkSectionLoading,
    reorderWorkSections,
    reorderWorkSectionsLoading,
  } = useWorkSection({ page: "" });

  const { project, projectLoading } = useProject({ page: "projects" });

  const [sectionsOrder, setSectionsOrder] = useState([]);
  const [showOrder, setShowOrder] = useState(true);
  const [showProjectOrder, setShowProjectOrder] = useState(true);

  useEffect(() => {
    if (Array.isArray(workSection)) {
      setSectionsOrder(
        workSection.map((ws) => ({ _id: ws?._id, name: ws?.name }))
      );
    }
  }, [workSection]);

  const moveSection = (index, direction) => {
    setSectionsOrder((prev) => {
      const current = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return prev;
      const temp = current[index];
      current[index] = current[targetIndex];
      current[targetIndex] = temp;
      return current;
    });
  };

  const handleSaveSectionOrder = () => {
    const orderedIds = sectionsOrder.map((s) => s._id);
    if (!orderedIds.length) return;
    reorderWorkSections(orderedIds, {
      onSuccess: () => {
        refetchWorkSection();
        addSuccess("Work Sections order saved.");
      },
      onError: (err) => {
        addError({
          text: `Error saving order: ${
            err?.response?.data?.msg || err.message
          }`,
        });
      },
    });
  };

  const moveProject = (index, direction) => {
    setInput((prev) => {
      const current = [...(prev?.projects || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return prev;
      const temp = current[index];
      current[index] = current[targetIndex];
      current[targetIndex] = temp;
      return { ...prev, projects: current };
    });
  };

  const handleSave = () => {
    if (!input?.name || !input?.projects?.length) {
      return addError({ text: "Fill all the details of the work section." });
    }

    if (newWorkSection)
      createWorkSection(input, {
        onSuccess: (data) => {
          refetchWorkSection();
          return addSuccess("New Work Section added successfully.");
        },
        onError: (err) => {
          addError({
            text: `Error in adding work section: ${err.response.data.msg}`,
          });
        },
      });
    else {
      if (!element)
        return addError({
          text: "Select a project to update or create new project.",
        });
      updateWorkSection(
        { workSectionId: element, body: input },
        {
          onSuccess: (data) => {
            refetchWorkSection();
            return addSuccess("Work Section updated successfully.");
          },
          onError: (err) => {
            addError({
              text: `Error in updating work section: ${err.response.data.msg}`,
            });
          },
        }
      );
    }
  };

  const handleDelete = () => {
    deleteWorkSection(
      { workSectionId: element },
      {
        onSuccess: (data) => {
          refetchWorkSection();
          return addSuccess("WorkSection deleted successfully.");
        },
        onError: (err) => {
          addError({
            text: `Error in deleting Work Section: ${err.response.data.msg}`,
          });
        },
      }
    );
  };

  useEffect(() => {
    if (element) {
      const ws = workSection?.find((ws) => ws?._id === element);
      setInput({
        name: ws?.name,
        projects: ws?.projects?.map((ws) => ws._id),
        link: ws?.link,
      });
    } else setInput({ name: "", projects: [], link: "" });

    setNewWorkSection(!element);
  }, [element]);

  useEffect(() => {
    if (newWorkSection) {
      setInput({ name: "", projects: [], link: "" });
      setElement("");
    }
  }, [newWorkSection]);

  return (
    <div className={styles.workSection}>
      <div className={styles.orderBox}>
        <div
          className={styles.orderHeader}
          role="button"
          aria-expanded={showOrder}
          onClick={() => setShowOrder((prev) => !prev)}
        >
          <Label>Reorder Work Sections</Label>
          <span className={styles.orderToggle}>{showOrder ? "▾" : "▸"}</span>
        </div>
        {showOrder && (
          <>
            <div className={styles.orderList}>
              {sectionsOrder?.map((s, idx) => (
                <div key={s._id} className={styles.orderItem}>
                  <div className={styles.orderIndex}>{idx + 1}</div>
                  <div className={styles.orderName} title={s.name}>
                    {s.name}
                  </div>
                  <div className={styles.orderControls}>
                    <button
                      type="button"
                      className={styles.moveBtn}
                      onClick={() => moveSection(idx, -1)}
                      disabled={idx === 0}
                      aria-label="Move up"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className={styles.moveBtn}
                      onClick={() => moveSection(idx, 1)}
                      disabled={idx === sectionsOrder.length - 1}
                      aria-label="Move down"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.orderActions}>
              <ThemedButton
                theme={"PRIMARY"}
                onClick={handleSaveSectionOrder}
                width={"200px"}
                height={"34px"}
                className={styles.btn}
                loading={reorderWorkSectionsLoading}
              >
                Save Sections Order
              </ThemedButton>
            </div>
          </>
        )}
      </div>

      <div className={styles.toggleBox}>
        {" "}
        <div className={styles.toggleHeading}>Create new work section</div>
        <Toggle
          checked={newWorkSection}
          theme={"PURPLE"}
          onChange={() => setNewWorkSection((prev) => !prev)}
          className={styles.toggle}
        />
        {element && (
          <ThemedButton
            theme={"RED"}
            onClick={() => handleDelete()}
            width={"100px"}
            height={"30px"}
            loading={deleteWorkSectionLoading}
            className={styles.delBtn}
          >
            Delete
          </ThemedButton>
        )}
      </div>

      <div className={styles.nameBox}>
        <Label>Name</Label>
        <Input value={input} setValue={setInput} name={"name"} />
      </div>
      <div className={styles.nameBox}>
        <Label>Link</Label>
        <Input value={input} setValue={setInput} name={"link"} />
      </div>
      <div className={styles.selectProject}>
        <Label>Select Project for section</Label>
        <Select
          value={input}
          setValue={setInput}
          name={"projects"}
          isMulti
          options={project?.map((p) => ({ label: p.name, value: p._id }))}
          loading={projectLoading}
          isSearchable
          height={"50px"}
        />
      </div>
      {/* {!!input?.projects?.length && (
        <div className={styles.orderBox}>
          <div
            className={styles.orderHeader}
            role="button"
            aria-expanded={showProjectOrder}
            onClick={() => setShowProjectOrder((prev) => !prev)}
          >
            <Label>Order selected projects</Label>
            <span className={styles.orderToggle}>
              {showProjectOrder ? "▾" : "▸"}
            </span>
          </div>
          {showProjectOrder && (
            <div className={styles.orderList}>
              {input?.projects?.map((projectId, idx) => {
                const p = project?.find((pr) => pr?._id === projectId);
                return (
                  <div key={projectId} className={styles.orderItem}>
                    <div className={styles.orderIndex}>{idx + 1}</div>
                    <div className={styles.orderName} title={p?.name}>
                      {p?.name || projectId}
                    </div>
                    <div className={styles.orderControls}>
                      <button
                        type="button"
                        className={styles.moveBtn}
                        onClick={() => moveProject(idx, -1)}
                        disabled={idx === 0}
                        aria-label="Move up"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className={styles.moveBtn}
                        onClick={() => moveProject(idx, 1)}
                        disabled={idx === input.projects.length - 1}
                        aria-label="Move down"
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )} */}

      <ThemedButton
        theme={"PRIMARY"}
        onClick={() => handleSave()}
        width={"100%"}
        height={"40px"}
        className={styles.btn}
        loading={createWorkSectionLoading || updateWorkSectionLoading}
      >
        {newWorkSection ? "Create Work Section" : "Update Work Section"}
      </ThemedButton>
    </div>
  );
};

export default WorkSections;
